import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Globe, Clock, ArrowLeft, Mail, Users, CheckCircle2, CalendarDays, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { api, fileUrl, errMsg } from "../lib/api";
import { ListingHeroImg } from "../lib/termImages";
import { ActivityCard } from "../components/Cards";
import { Reveal, SectionTitle, useSeo } from "../components/VIP";
import { ListingActions } from "../components/ListingActions";
import { Paged } from "../components/PagedList";
import { Sponsors } from "../components/Marketplace";
import { VideoGrid } from "../components/VideoGrid";
import { DealsSection, MerchGrid } from "../components/VenueExtras";
import { ReviewsSection } from "../components/Reviews";
import LoyaltyJoinCard from "../components/LoyaltyJoinCard";
import { NominateButton, AwardBadges, VoteCard } from "../components/AwardsWidgets";
import { WeatherPanel } from "../components/Weather";
import { useAuth } from "../context/AuthContext";
import { PipeText } from "../lib/pipeText";

const MAP = (q) => `https://maps.google.com/maps?q=${encodeURIComponent(q)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;

const inp = "w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-rose-500 text-sm";
const lbl = "text-[11px] uppercase tracking-wider text-zinc-500 font-semibold";

function fmtD(d) {
  try { return new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); } catch { return d; }
}
function fmtT(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ap}`;
}

function BookingPanel({ a }) {
  const { user } = useAuth();
  const schedules = a.schedules || [];
  const dates = useMemo(() => [...new Set(schedules.map((s) => s.date))], [schedules]);
  const [date, setDate] = useState(dates[0] || "");
  const [sid, setSid] = useState("");
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", participants: 2, special_requests: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (!date && dates.length) setDate(dates[0]); }, [dates, date]);
  const times = schedules.filter((s) => s.date === date);
  const sched = schedules.find((s) => s.id === sid);
  const price = parseFloat(String(sched?.price_override || a.price || "").replace(/[^0-9.]/g, "")) || 0;
  const total = price * (parseInt(form.participants) || 1);

  if (a.booking_mode === "external") {
    return (
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="booking-panel">
        <h3 className="font-display font-bold text-lg">Book this activity</h3>
        {a.price && <p className="text-2xl font-display font-bold text-rose-400">{String(a.price).startsWith("$") ? a.price : `$${a.price}`}<span className="text-sm text-zinc-500 font-normal"> /person</span></p>}
        <a href={a.booking_link || a.website} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full bg-rose-500 hover:bg-rose-600 font-bold rounded-full py-3" data-testid="book-external-btn">
          Book Now <ExternalLink size={15} />
        </a>
        <p className="text-xs text-zinc-500 text-center">Booking handled on the provider's website.</p>
      </div>
    );
  }

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/activities/${a.id}/book`, { ...form, schedule_id: sid, date, participants: parseInt(form.participants) || 1 });
      setDone(r.data);
      toast.success("Booking request sent!");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="bg-[#111] border border-emerald-500/30 rounded-2xl p-6 text-center space-y-3" data-testid="booking-confirmed">
        <CheckCircle2 size={36} className="text-emerald-400 mx-auto" />
        <h3 className="font-display font-bold text-lg">Request received!</h3>
        <p className="text-sm text-zinc-400">The provider will confirm your booking{done.schedule_date ? ` for ${fmtD(done.schedule_date)}` : ""}. A confirmation will appear {done.user_id ? "in your dashboard" : "via email"}.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-4" data-testid="booking-panel">
      <h3 className="font-display font-bold text-lg">Request a booking</h3>
      {a.price && <p className="text-2xl font-display font-bold text-rose-400">{String(a.price).startsWith("$") ? a.price : `$${a.price}`}<span className="text-sm text-zinc-500 font-normal"> /person</span></p>}
      {dates.length > 0 ? (
        <>
          <div>
            <p className={lbl}>Pick a date</p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {dates.slice(0, 10).map((d) => (
                <button key={d} onClick={() => { setDate(d); setSid(""); }} data-testid={`book-date-${d}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${date === d ? "bg-rose-500 border-rose-500 font-bold" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
                  {fmtD(d)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className={lbl}>Available times</p>
            <div className="space-y-1.5 mt-1.5">
              {times.map((s) => {
                const soldOut = s.status === "sold_out" || (s.remaining !== null && s.remaining <= 0);
                return (
                  <button key={s.id} disabled={soldOut} onClick={() => setSid(s.id)} data-testid={`book-time-${s.id}`}
                    className={`w-full flex items-center justify-between text-sm px-3 py-2.5 rounded-lg border ${sid === s.id ? "bg-rose-500/15 border-rose-500" : "border-white/10 hover:border-white/30"} ${soldOut ? "opacity-40" : ""}`}>
                    <span>{fmtT(s.start_time)}{s.end_time ? ` – ${fmtT(s.end_time)}` : ""}</span>
                    <span className={`text-xs ${soldOut ? "text-rose-300" : s.status === "limited" ? "text-amber-300" : "text-zinc-500"}`}>
                      {soldOut ? "Sold out" : s.remaining !== null ? `${s.remaining} spots left` : "Available"}
                    </span>
                  </button>
                );
              })}
              {times.length === 0 && <p className="text-xs text-zinc-500">No times on this date.</p>}
            </div>
          </div>
        </>
      ) : (
        <p className="text-xs text-zinc-500">No published schedule yet — send a request and the provider will follow up with times.</p>
      )}
      <div className="grid grid-cols-2 gap-2.5">
        <div className="col-span-2"><p className={lbl}>Your name *</p><input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="book-name" /></div>
        <div><p className={lbl}>Email *</p><input className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" /></div>
        <div><p className={lbl}>Phone</p><input className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" /></div>
        <div>
          <p className={lbl}><Users size={11} className="inline mr-1" />Participants</p>
          <input type="number" min="1" className={inp} value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} data-testid="book-participants" />
        </div>
        <div className="flex items-end pb-1">
          {total > 0 && <p className="text-sm">Total: <span className="font-display font-bold text-rose-400">${total.toFixed(2)}</span></p>}
        </div>
        <div className="col-span-2"><p className={lbl}>Special requests</p><textarea className={inp} rows={2} value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} data-testid="book-requests" /></div>
      </div>
      <button onClick={submit} disabled={busy} className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 font-bold rounded-full py-3" data-testid="book-submit-btn">
        {busy && <Loader2 size={15} className="animate-spin" />} Request Booking
      </button>
      <p className="text-[11px] text-zinc-500 text-center">No payment now — the provider confirms your request first.</p>
    </div>
  );
}

const Info = ({ Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <Icon size={16} className="text-rose-400 mt-0.5 shrink-0" />
    <div><p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p><div className="text-sm text-zinc-200">{children}</div></div>
  </div>
);

export default function ActivityDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/activities/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true)); window.scrollTo(0, 0); }, [slug]);
  useSeo(a ? (a.seo_title || `${a.name} | Gulf Coast VIP`) : "Activity", a?.seo_description || a?.short_description);

  if (err) return <div className="py-32 text-center text-zinc-500">Activity not found. <Link to="/activities" className="text-rose-400">Browse activities</Link></div>;
  if (!a) return <div className="py-32 text-center text-zinc-600 animate-pulse">Loading…</div>;

  const textSection = (kicker, title, text) => text && (
    <Reveal><SectionTitle kicker={kicker} title={title} /><PipeText text={text} className="text-zinc-300 leading-relaxed" /></Reveal>
  );

  return (
    <div data-testid="activity-detail">
      <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
        <ListingHeroImg ent={a} kind="activity" className="absolute inset-0 w-full h-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
        <div className="relative px-6 md:px-12 lg:px-20 pb-8 pt-28">
          <Link to="/activities" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-3"><ArrowLeft size={14} /> All activities</Link>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {a.activity_type && <span className="bg-rose-500 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">{a.activity_type}</span>}
            {a.duration && <span className="glass text-[11px] px-2.5 py-1 rounded-full"><Clock size={11} className="inline mr-1" />{a.duration}</span>}
            {a.family_friendly && <span className="glass text-[11px] px-2.5 py-1 rounded-full">Family friendly</span>}
            {a.difficulty && <span className="glass text-[11px] px-2.5 py-1 rounded-full">{a.difficulty}</span>}
            {a.award_badges?.length > 0 && <AwardBadges badges={a.award_badges} />}
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl">{a.name}</h1>
          <p className="text-zinc-300 mt-2 flex items-center gap-1.5">
            <MapPin size={15} /> {[a.meeting_location || a.address, a.city, a.state].filter(Boolean).join(", ")}
            {a.business ? <Link to={`/businesses/${a.business.slug}`} className="text-rose-400 hover:underline" data-testid="activity-business-link">· by {a.business.name}</Link> : a.provider_name && <span className="text-zinc-500">· by {a.provider_name}</span>}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <ListingActions entityType="activity" entityId={a.id} claimStatus={a.claim_status || "unclaimed"} verified={!!a.verified} />
            <NominateButton entityType="activity" entity={a} />
          </div>
        </div>
      </section>

      <LoyaltyJoinCard entityType="activity" entityId={a.id} />

      <div className="px-6 md:px-12 lg:px-20 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          <div className="lg:hidden"><VoteCard entityType="activity" entity={a} /></div>
          {textSection("About", "Overview", a.description || a.short_description)}
          {textSection("Included", "What's Included", a.whats_included)}
          {textSection("Prepare", "What to Bring", a.what_to_bring)}
          {textSection("Good to know", "Restrictions", a.restrictions)}

          {(a.cancellation_policy || a.weather_policy || a.safety_info || a.waiver_required) && (
            <Reveal><SectionTitle kicker="Policies" title="Policies & Safety" />
              <div className="space-y-3 text-sm text-zinc-300">
                {a.cancellation_policy && <p><strong className="text-white">Cancellation:</strong> {a.cancellation_policy}</p>}
                {a.weather_policy && <p><strong className="text-white">Weather:</strong> {a.weather_policy}</p>}
                {a.waiver_required && <p className="text-amber-300"><ShieldCheck size={14} className="inline mr-1" />A signed waiver is required to participate.</p>}
                {a.safety_info && <PipeText text={a.safety_info} />}
              </div></Reveal>
          )}

          {a.deals?.length > 0 && <Reveal><Paged items={a.deals} perKey="activity_deals_pp" perDefault={4} label="deals" testid="activity-deals-paged" render={(slice) => <DealsSection deals={slice} />} /></Reveal>}

          {a.marketplace_merch?.length > 0 && <Reveal><Paged items={a.marketplace_merch} perKey="activity_merch_pp" perDefault={6} label="items" testid="activity-merch-paged" render={(slice) => <MerchGrid items={slice} title="Shop & Extras" />} /></Reveal>}

          {a.business && (
            <Reveal>
              <SectionTitle kicker="Operated by" title="The Business & Directions" />
              <Link to={`/businesses/${a.business.slug}`} className="group block rounded-2xl overflow-hidden bg-[#111] border border-white/10 card-hover mt-6" data-testid="activity-business-card">
                <div className="relative h-52 overflow-hidden">
                  {a.business.hero_image
                    ? <img src={fileUrl(a.business.hero_image)} alt={a.business.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full bg-gradient-to-br from-rose-500/30 via-black to-black" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                  {a.business.logo && <img src={fileUrl(a.business.logo)} alt="" className="absolute top-4 left-4 w-14 h-14 rounded-xl object-cover border border-white/20 bg-black/40 backdrop-blur" />}
                  <div className="absolute bottom-0 p-5">
                    {a.business.business_type && <p className="overline">{a.business.business_type}</p>}
                    <h3 className="font-display font-bold text-2xl">{a.business.name}</h3>
                    {(a.business.address || a.business.city) && (
                      <p className="text-zinc-300 text-sm mt-1 flex items-center gap-1"><MapPin size={13} /> {[a.business.address, a.business.city, a.business.state].filter(Boolean).join(", ")}</p>
                    )}
                    {a.business.short_description && <p className="text-zinc-400 text-sm mt-2 line-clamp-2 max-w-xl">{a.business.short_description}</p>}
                  </div>
                </div>
              </Link>
              <div className="mt-5 rounded-2xl overflow-hidden border border-white/10">
                <iframe title="business-map" src={MAP([a.business.address, a.business.city, a.business.state].filter(Boolean).join(", ") || `${a.city}, ${a.state}`)} className="w-full h-72 grayscale invert-[.9]" loading="lazy" />
              </div>
            </Reveal>
          )}

          {(a.gallery_images || []).length > 0 && (
            <Reveal><SectionTitle kicker="In frame" title="Gallery" />
              <Paged items={a.gallery_images} perKey="activity_gallery_pp" perDefault={12} label="photos" testid="activity-gallery" render={(slice) => (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {slice.map((g, i) => <img key={`${typeof g === "string" ? g : g.image_url || g.url}-${i}`} src={fileUrl(typeof g === "string" ? g : g.image_url || g.url)} alt="" className="w-full aspect-square object-cover rounded-xl" loading="lazy" />)}
                </div>
              )} /></Reveal>
          )}

          {a.videos?.length > 0 && <Reveal><Paged items={a.videos} perKey="activity_videos_pp" perDefault={6} label="videos" testid="activity-videos" render={(slice) => <VideoGrid items={slice} />} /></Reveal>}
          {a.sponsors?.length > 0 && <Reveal><Sponsors items={a.sponsors} /></Reveal>}
          <ReviewsSection entityType="activity" entityId={a.id} ownerUserId={a.owner_user_id} />

          {(a.related_activities || []).length > 0 && (
            <Reveal><SectionTitle kicker="More like this" title="Related Activities" />
              <Paged items={a.related_activities} perKey="activity_related_pp" perDefault={4} label="activities" testid="activity-related" render={(slice) => (
                <div className="grid sm:grid-cols-2 gap-4">{slice.map((r) => <ActivityCard key={r.id} a={r} />)}</div>
              )} /></Reveal>
          )}
        </div>

        <aside className="space-y-6">
          <div className="hidden lg:block"><VoteCard entityType="activity" entity={a} compact /></div>
          <BookingPanel a={a} />
          <WeatherPanel city={a.city} state={a.state} weatherPolicy={a.weather_policy} />
          <div className="bg-[#111] border border-white/10 rounded-2xl p-6 space-y-5" data-testid="activity-info-panel">
            <h3 className="font-display font-bold text-lg">Details</h3>
            {a.duration && <Info Icon={Clock} label="Duration">{a.duration}</Info>}
            {a.min_age && <Info Icon={ShieldCheck} label="Minimum age">{a.min_age}</Info>}
            {(a.max_group_size || a.min_group_size) && <Info Icon={Users} label="Group size">{[a.min_group_size && `min ${a.min_group_size}`, a.max_group_size && `max ${a.max_group_size}`].filter(Boolean).join(" · ")}</Info>}
            {a.languages && <Info Icon={Globe} label="Languages">{a.languages}</Info>}
            {a.child_price && <Info Icon={CalendarDays} label="Child price">${String(a.child_price).replace(/^\$/, "")}</Info>}
            {a.group_price && <Info Icon={Users} label="Group price">${String(a.group_price).replace(/^\$/, "")}</Info>}
            {a.accessibility_info && <Info Icon={ShieldCheck} label="Accessibility">{a.accessibility_info}</Info>}
            {a.phone && <Info Icon={Phone} label="Phone"><a href={`tel:${a.phone}`} className="hover:text-rose-400">{a.phone}</a></Info>}
            {a.email && <Info Icon={Mail} label="Email"><a href={`mailto:${a.email}`} className="hover:text-rose-400">{a.email}</a></Info>}
            {a.website && <Info Icon={Globe} label="Website"><a href={a.website} target="_blank" rel="noreferrer" className="hover:text-rose-400 break-all">{a.website}</a></Info>}
          </div>
        </aside>
      </div>
    </div>
  );
}
